import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface FormData {
  password: string;
  confirmPassword: string;
}

interface ApiResponse {
  message: string;
}

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      setError(t('auth.resetPassword.error'));
    }
  }, [token, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.password) {
      setError(t('auth.signup.passwordRequired'));
      return false;
    }

    if (formData.password.length < 6) {
      setError(t('auth.resetPassword.passwordTooShort'));
      return false;
    }

    if (!formData.confirmPassword) {
      setError(t('auth.signup.passwordRequired'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();

    if (!token) {
      setError(t('auth.resetPassword.error'));
      return;
    }

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: formData.password
        })
      });

      const data: ApiResponse = await response.json();

      if (!response.ok) {
        setError(data.message || t('auth.resetPassword.error'));
        return;
      }

      setSuccess(data.message || t('auth.resetPassword.success'));
      setFormData({ password: '', confirmPassword: '' });

      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.login.serverError');
      setError(t('auth.login.serverError') + ': ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">{t('auth.resetPassword.title')}</h1>
            <p className="text-gray-600 mt-2">
              {t('auth.resetPassword.subtitle')}
            </p>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center p-6 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <p className="text-green-700 text-center font-medium">
                  {success}
                </p>
                <p className="text-green-600 text-sm mt-2">
                  {t('auth.login.loginSuccess')}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Nouveau mot de passe */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.resetPassword.newPassword')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t('auth.resetPassword.passwordTooShort')}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    disabled={loading || !token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    disabled={loading || !token}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirmer mot de passe */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.resetPassword.confirmPassword')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={t('auth.signup.confirmYourPassword')}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    disabled={loading || !token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    disabled={loading || !token}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Messages d'erreur */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Bouton de réinitialisation */}
              <button
                onClick={handleSubmit}
                disabled={loading || !token}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                {loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.resetButton')}
              </button>
            </div>
          )}

          {/* Lien retour connexion */}
          <p className="text-center text-gray-600 text-sm mt-6">
            {t('auth.signup.haveAccount')}{' '}
            <a href="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
              {t('auth.login.title')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
